/**
 * RecurrenceBadge - Compact "this row is part of a recurring series"
 * marker, modeled on the EstimateBadge / EventTypeBadge atoms.
 *
 * Rendered on Work Order + Inspection list rows (and the detail page
 * header) when the row has a `recurrence_series_id` linkage. Surfaces
 * the recurrence summary as a tooltip so operators can see the cadence
 * without leaving the list — full management lives on the Repeats
 * pages.
 *
 * @example
 * <RecurrenceBadge summary="Every 2 weeks on Mon" />
 */
import React from 'react';
import { RecurrenceIcon } from '../RecurrenceIcon';

export interface RecurrenceBadgeProps {
	/**
	 * Human-readable schedule summary — e.g. the output of
	 * `formatRecurrenceSummary(rule)`. Surfaced via `title` (tooltip)
	 * and read out as the badge label for screen readers.
	 */
	summary?: string;
	/** Visual scale — 'sm' for list rows, 'md' for detail headers. */
	size?: 'sm' | 'md';
	/** Optional extra classes appended after defaults. */
	className?: string;
}

const SIZE_CLASSES: Record<NonNullable<RecurrenceBadgeProps['size']>, string> = {
	sm: 'text-11px px-6px py-1px gap-3px',
	md: 'text-12px px-8px py-2px gap-4px',
};

const ICON_SIZE: Record<NonNullable<RecurrenceBadgeProps['size']>, string> = {
	sm: 'w-11px h-11px',
	md: 'w-13px h-13px',
};

export const RecurrenceBadge: React.FC<RecurrenceBadgeProps> = ({
	summary,
	size = 'sm',
	className = '',
}) => {
	const tooltip = summary ? `Recurring · ${summary}` : 'Recurring';
	return (
		<span
			className={`recurrence-badge inline-flex items-center rounded-full bg-purple-50 text-purple-700 border border-purple-200 font-medium uppercase tracking-wide ${SIZE_CLASSES[size]} ${className}`}
			data-ref="atom-recurrence-badge"
			title={tooltip}
			aria-label={tooltip}
		>
			<RecurrenceIcon className={`recurrence-badge__icon ${ICON_SIZE[size]} text-purple-700`} />
			<span className="recurrence-badge__label">Recurring</span>
		</span>
	);
};

export default RecurrenceBadge;
